import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type TabId = 'today' | 'plan' | 'money' | 'tools';

/**
 * The app's four-tab bar, drawn at device scale.
 *
 * The four tabs are the app's whole information architecture and the spine of
 * this page: Today answers what is safe to spend, Plan holds the budget over
 * the period, Money holds accounts and net worth, Tools holds everything else.
 * Labels are the shipped strings (`tabToday`, `tabPlan`, `tabMoney`,
 * `tabTools`).
 *
 * Label is 10px/400, accent when active and neutral-500 otherwise (spec §D.1).
 * In light the active tab splits icon and label colour: icon accent-600,
 * label accent-700 (spec §F.2.4), which the token mapping already does.
 */
@Component({
  selector: 'app-tab-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="bar">
      @for (tab of tabs; track tab.id) {
      <span class="tab" [class.tab--active]="tab.id === active()">
        <svg class="tab__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path [attr.d]="tab.path" />
        </svg>
        <span class="tab__label">{{ tab.label }}</span>
      </span>
      }
    </nav>
  `,
  styles: [
    `
      .bar {
        display: flex;
        align-items: flex-start;
        justify-content: space-around;
        padding: 8.4px 11.2px 22.4px;
        background: var(--color-surface);
        border-top: 1px solid var(--color-line);
      }

      .tab {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        color: var(--color-neutral-500);
        flex: 1;
      }

      .tab--active {
        color: var(--color-accent);
      }

      .tab__icon {
        width: 21px;
        height: 21px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.5;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .tab--active .tab__icon {
        stroke: var(--color-accent-stroke);
      }

      .tab__label {
        font-size: 10px;
        font-weight: 400;
        line-height: 1;
      }
    `,
  ],
})
export class TabBarComponent {
  readonly active = input.required<TabId>();

  /**
   * The labels are translated, because the app is offered in the same five
   * languages this site is. An earlier version left them in English on the
   * reasoning that the app only spoke English and French, so a German page
   * showing a German phone would have claimed a language the build did not
   * have. That is no longer true.
   */
  protected readonly tabs: { id: TabId; label: string; path: string }[] = [
    {
      id: 'today',
      label: $localize`:@@tabToday:Today`,
      path: 'M12 7v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
    },
    {
      id: 'plan',
      label: $localize`:@@tabPlan:Plan`,
      path: 'M4 19V9M10 19V5M16 19v-6M4 19h16',
    },
    {
      id: 'money',
      label: $localize`:@@tabMoney:Money`,
      path: 'M3 8h18v11H3zM3 8l3-3h12l3 3M12 12.5h3',
    },
    {
      id: 'tools',
      label: $localize`:@@tabTools:Tools`,
      path: 'M4 7h16M4 12h16M4 17h10',
    },
  ];
}
